import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { auth } from '../firebase';
import { isMobilePhone } from '../utils/deviceDetection';
import ChatWidget from './ChatWidget';
import { isAdmin, isPartner, getAllowedScenarios, hasAccess } from '../config/accessControl';
import UserAccessManager from './UserAccessManager';
import { trackVisit } from '../services/analytics';

// Add scenarios data
const AVAILABLE_SCENARIOS = [
  { id: 'smartmed', name: 'SmartMed' }
  // Add other scenarios here
];

const Home = () => {
  const navigate = useNavigate();
  const [userEmail, setUserEmail] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [scenarios, setScenarios] = useState(AVAILABLE_SCENARIOS);
  const [isLoading, setIsLoading] = useState(true);
  const [userAccessLevel, setUserAccessLevel] = useState(null);
  const [showUserManager, setShowUserManager] = useState(false);
  const [demoScenario, setDemoScenario] = useState(null);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (user) {
        setUserEmail(user.email);
        // We need to track visit with a specific scenario
        const access = await hasAccess(user.email);
        if (access) {
          // Get allowed scenarios for this user
          const scenarios = await getAllowedScenarios(user.email);
          if (scenarios === 'all' || scenarios.length > 0) {
            // Track visit for each accessible scenario
            const scenariosToTrack = scenarios === 'all' ? AVAILABLE_SCENARIOS.map(s => s.id) : scenarios;
            for (const scenario of scenariosToTrack) {
              await trackVisit(scenario, user.email);
            }
          }
        }
        // Check if user has explicit access
        if (!access) {
          setUserAccessLevel('visitor');
        } else {
          // Check if admin
          const adminStatus = await isAdmin(user.email);
          setUserAccessLevel(adminStatus ? 'admin' : 'partner');
        }
      } else {
        setUserEmail(null);
        navigate('/login');
      }
      setIsLoading(false);
    });

    setIsMobile(isMobilePhone());

    return () => unsubscribe();
  }, [navigate]);
  
  const handleLogout = () => {
    signOut(auth).then(() => {
      navigate('/login');
    }).catch((error) => {
      console.error('Error signing out: ', error);
    });
  };

  const handleShowInstructions = async () => {
    // Only allow admins to see instructions
    const adminStatus = await isAdmin(userEmail);
    if (!adminStatus) {
        setChatMessages(prevMessages => [...prevMessages, {
            sender: 'System',
            message: 'Only administrators have access to system instructions.'
        }]);
        return;
    }
    // ... rest of instructions handling
  };

  const handleControlsClick = async () => {
    const adminStatus = await isAdmin(userEmail);
    if (!adminStatus) {
      setChatMessages(prevMessages => [...prevMessages, {
        sender: 'System',
        message: 'You do not have sufficient access rights to view the controls.'
      }]);
      return;
    }
    // ... existing controls code ...
  };

  // Filter scenarios in dropdown based on access
  const getAvailableScenarios = () => {
    const allowedScenarios = getAllowedScenarios(userEmail);
    if (allowedScenarios === 'all') return scenarios;
    return scenarios.filter(scenario => allowedScenarios.includes(scenario.id));
  };

  const updateDemoScenario = async (companyName) => {
    try {
        // First update in main database
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/demo-scenario`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-Access-Token': process.env.REACT_APP_CONNECTOR_API_KEY
            },
            body: JSON.stringify({ company_name: companyName })
        });

        if (response.ok) {
            // Then trigger immediate context update
            const updateResponse = await fetch(`${process.env.REACT_APP_AI_ENDPOINT_URL}/update-company`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ company_name: companyName })
            });

            if (updateResponse.ok) {
                setDemoScenario(companyName);
            } else {
                throw new Error(`Failed to update context: ${updateResponse.statusText}`);
            }
        } else {
            throw new Error(`Failed to update demo scenario: ${response.statusText}`);
        }
    } catch (error) {
        console.error('Error updating demo scenario:', error);
    }
  };

  // Add useEffect to refresh context on page load
  useEffect(() => {
    const refreshContextOnLoad = async () => {
        if (demoScenario) {
            try {
                await fetch(`${process.env.REACT_APP_AI_ENDPOINT_URL}/update-company`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({ company_name: demoScenario })
                });
            } catch (error) {
                console.error('Error refreshing context:', error);
            }
        }
    };
    refreshContextOnLoad();
  }, [demoScenario]);

  if (isLoading) {
    return <div>Loading...</div>;
  }

  // Render based on access level
  if (userAccessLevel === 'visitor') {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-100">
        <h1 className="text-6xl font-bold mb-6 bg-gradient-to-r from-blue-500 to-teal-500 text-transparent bg-clip-text">
          Welcome to Mednavigator AI
        </h1>
        <p className="text-2xl text-gray-600 mb-10">Public documentation will be available soon...</p>
        <div className="mt-4">
          <button 
            onClick={handleLogout} 
            className="px-6 py-3 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition duration-300 text-lg"
          >
            Log Out
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-gray-100">
      <h1 className="text-6xl font-bold mb-6 bg-gradient-to-r from-blue-500 to-teal-500 text-transparent bg-clip-text">
        Welcome to Mednavigator AI
      </h1>

      {/* Show different content based on user level */}
      {userAccessLevel === 'admin' ? (
        <>
          <p className="text-2xl text-gray-600 mb-10">Public documentation will be available soon...</p>
          <div className="space-x-6 mb-6">
            <Link to="/controls" className="px-6 py-3 bg-blue-400 text-white rounded-lg hover:bg-blue-500 transition duration-300 text-lg">
              Go to Controls
            </Link>
            <button 
              onClick={() => setShowUserManager(!showUserManager)} 
              className="px-6 py-3 bg-blue-400 text-white rounded-lg hover:bg-blue-500 transition duration-300 text-lg"
            >
              {showUserManager ? 'Hide User Manager' : 'Manage Users'}
            </button>
          </div>
        </>
      ) : (
        <>
          <p className="text-2xl text-gray-600 mb-6">Welcome, Partner!</p>
          <div className="bg-white rounded-lg shadow-md p-6 mb-8 max-w-2xl w-full">
            <h2 className="text-xl font-semibold mb-4">Your Access Details</h2>
            <p className="text-gray-600 mb-2">Email: {userEmail}</p>
            <p className="text-gray-600">Access Level: Partner</p>
          </div>
          <div className="mb-6">
            <Link 
              to="/controls" 
              className="px-6 py-3 bg-blue-400 text-white rounded-lg hover:bg-blue-500 transition duration-300 text-lg"
            >
              Go to Control Panel
            </Link>
          </div>
          <p className="text-gray-500 italic">API documentation will be available soon...</p>
        </>
      )}

      {/* Show logout button for all users */}
      <div className="mt-4">
        <button 
          onClick={handleLogout} 
          className="px-6 py-3 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition duration-300 text-lg"
        >
          Log Out
        </button>
      </div>

      {userAccessLevel === 'admin' && showUserManager && (
        <div className="mt-8 w-full max-w-4xl">
          <UserAccessManager />
        </div>
      )}
  
      <ChatWidget userEmail={userEmail} />
    </div>
  );
};

export default Home;